<script setup lang="ts">
import {useSystemMessageStore, ISystemMessageInstance} from '@toolify/client/src/stores/SystemMessageStore/useSystemMessageStore'
import {computed} from 'vue'
import {useI18n} from 'vue-i18n'
import MarkdownIt from 'markdown-it'
import ButtonComponent from '@toolify/client/src/modules/ui/components/ButtonComponent.vue'
import IconComponent from '@toolify/client/src/modules/ui/components/IconComponent.vue'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {IconWeightType} from '@toolify/client/src/modules/ui/enum/IconWeightType'
import {SizeVariantType} from '@toolify/client/src/modules/ui/enum/SizeVariantType'
import {StyleVariantType} from '@toolify/client/src/modules/ui/enum/StyleVariantType'
import {ColorVariantType} from '@toolify/client/src/modules/ui/enum/ColorVariantType'
import {SystemMessagePriorityType} from '@toolify/server/src/enum/SystemMessagePriorityType'

interface Props {
  priority?: SystemMessagePriorityType
}

const props = defineProps<Props>()

const {t} = useI18n()
const systemMessageStore = useSystemMessageStore()

const markdown = new MarkdownIt({
  html: false,
  breaks: true,
  linkify: true,
})

const visibleMessage = computed(() => systemMessageStore.visibleSystemMessage)
const messageCount = computed(() => systemMessageStore.visibleSystemMessageCount)
const messagePosition = computed(() => systemMessageStore.visibleSystemMessageIndex + 1)
const hasMultipleMessages = computed(() => messageCount.value > 1)

const effectivePriority = computed<SystemMessagePriorityType>(() => {
  return props.priority ?? visibleMessage.value?.priority ?? SystemMessagePriorityType.Neutral
})

const closeButtonColor = computed<ColorVariantType>(() => {
  return effectivePriority.value === SystemMessagePriorityType.Serious ? ColorVariantType.Error : ColorVariantType.Primary
})

const closeButtonStyle = computed<StyleVariantType>(() => {
  return effectivePriority.value === SystemMessagePriorityType.Serious ? StyleVariantType.FilledFilled : StyleVariantType.ClearFilled
})

function renderMarkdown(text: string): string {
  return markdown.render(text || '')
}

function bannerIconName(msg: ISystemMessageInstance): IconNameType {
  return msg.iconNameType ?? IconNameType.InfoCircle
}
</script>

<template>
  <div
    v-if="visibleMessage"
    class="system-message-banners tw-relative tw-z-[1000] tw-w-full tw-flex tw-flex-col"
  >
    <div :class="['system-message-banner', `priority-${effectivePriority}`, 'tw-w-full']">
      <div class="banner-inner tw-relative tw-w-full">
        <div class="banner-row tw-flex tw-items-center tw-gap-4 tw-w-full tw-px-2 tw-py-1">
          <div class="system-message-banner__icon tw-flex-shrink-0 tw-w-sm tw-h-sm tw-rounded-full tw-flex tw-items-center tw-justify-center">
            <IconComponent
              :name="bannerIconName(visibleMessage)"
              :weightType="IconWeightType.Solid"
              :size="SizeVariantType.Small"
              :color="ColorVariantType.Neutral"
            />
          </div>
          <div class="banner-text tw-flex-1 tw-min-w-0 tw-px-2 sm:tw-px-6 tw-flex tw-justify-center tw-items-center">
            <div class="markdown-content tw-text-center" v-html="renderMarkdown(visibleMessage.content)"/>
          </div>
          <div class="tw-flex-shrink-0 tw-ml-auto tw-flex tw-items-center tw-gap-1">
            <template v-if="hasMultipleMessages">
              <ButtonComponent
                :iconNameType="IconNameType.ChevronLeft"
                :iconWeightType="IconWeightType.Solid"
                :size="SizeVariantType.Medium"
                :style="closeButtonStyle"
                :color="closeButtonColor"
                round
                :aria-label="t('systemMessage.previous')"
                @click="systemMessageStore.showPreviousSystemMessage()"
              />
              <span
                class="system-message-banner__counter tw-tabular-nums"
                aria-live="polite"
              >
                {{t('systemMessage.counter', {current: messagePosition, total: messageCount})}}
              </span>
              <ButtonComponent
                :iconNameType="IconNameType.ChevronRight"
                :iconWeightType="IconWeightType.Solid"
                :size="SizeVariantType.Medium"
                :style="closeButtonStyle"
                :color="closeButtonColor"
                round
                :aria-label="t('systemMessage.next')"
                @click="systemMessageStore.showNextSystemMessage()"
              />
            </template>
            <ButtonComponent
              :iconNameType="IconNameType.Xmark"
              :iconWeightType="IconWeightType.Solid"
              :size="SizeVariantType.Standard"
              :style="closeButtonStyle"
              :color="closeButtonColor"
              round
              :aria-label="hasMultipleMessages ? t('systemMessage.closeAll') : t('systemMessage.close')"
              :title="hasMultipleMessages ? t('systemMessage.closeAll') : t('systemMessage.close')"
              @click="systemMessageStore.acknowledgeAll()"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '../../../styles/variables' as variables;

.system-message-banner {
  backdrop-filter: blur(8px);

  @media (max-width: variables.$breakpointLargeMax) {
    padding-top: calc(#{variables.$safeAreaTop} + #{map-get(variables.$spacing, space-2)});
    padding-left: variables.$safeAreaLeft;
    padding-right: variables.$safeAreaRight;
  }

  &.priority-neutral {
    background: map-get(variables.$colors, primary-container);
    color: map-get(variables.$colors, on-primary-container);
  }

  &.priority-serious {
    background: map-get(variables.$colors, error);
    color: map-get(variables.$colors, on-error);
  }
}

@media (max-width: variables.$breakpointLargeMax) {
  .banner-row {
    align-items: flex-start;
    gap: map-get(variables.$spacing, space-2);
  }

  .banner-text {
    justify-content: flex-start;
    padding-left: 0;
    padding-right: 0;
  }

  .markdown-content {
    text-align: left;
    max-height: 40dvh;
    overflow-y: auto;
  }
}

.system-message-banner__counter {
  min-width: 2.5rem;
  text-align: center;
  font-size: map-get(variables.$textSizes, text-sm);
  font-weight: 500;
  color: inherit;
}

.system-message-banner__icon :deep(.icon) {
  color: inherit;
}

.markdown-content {
  width: 100%;
  line-height: 125%;
  overflow-wrap: anywhere;

  &:deep(*) {
    min-width: 0;
  }

  &:deep(p) {
    margin: 0;
    font-size: map-get(variables.$textSizes, text-base);
  }

  &:deep(p + p) {
    margin-top: 0.5rem;
  }

  &:deep(ul),
  &:deep(ol),
  &:deep(menu) {
    margin: 0.5rem 0 0;
    padding-left: 1.1rem;
    list-style-position: outside;
  }

  &:deep(ul),
  &:deep(menu) {
    list-style-type: disc;
  }

  &:deep(ol) {
    list-style-type: decimal;
  }

  &:deep(li + li) {
    margin-top: 0.2rem;
  }

  &:deep(h1),
  &:deep(h2),
  &:deep(h3),
  &:deep(h4),
  &:deep(h5),
  &:deep(h6) {
    margin: 0 0 0.3rem;
    font-weight: 600;
    line-height: 1.25;
    font-size: map-get(variables.$textSizes, text-lg);
  }

  &:deep(pre) {
    margin: 0.5rem 0 0;
    padding: map-get(variables.$spacing, space-2);
    overflow-x: auto;
    border-radius: map-get(variables.$radiuses, radius-sm);
    background: rgba(0, 0, 0, 0.18);
    color: currentColor;
  }

  &:deep(code) {
    font-size: 0.85em;
    border-radius: 0.2rem;
    background: rgba(0, 0, 0, 0.18);
    color: currentColor;
    padding: 0.075rem 0.2rem;
  }

  &:deep(pre code) {
    padding: 0;
    border-radius: 0;
    background: transparent;
  }

  &:deep(blockquote) {
    margin: 0.5rem 0 0;
    padding-left: 0.6rem;
    border-left: 2px solid rgba(255, 255, 255, 0.35);
    color: rgba(255, 255, 255, 0.9);
  }

  &:deep(a) {
    color: inherit;
    text-decoration: underline;
  }

  &:deep(table) {
    margin: 0.2rem 0;
    border-spacing: 2px;
    border-collapse: separate;
    border-radius: variables.$borderRadius;
    overflow: hidden;

    td, th {
      padding: 0.2rem 0.4rem;
      text-align: left;
      background: rgba(255, 255, 255, 0.12);
    }
  }
}
</style>
